import { useState, useEffect } from "react"
import { Link } from "../lib/router"
import {
  User, Mail, Lock, Calendar, Droplets,
  Building2, MapPin, Eye, EyeOff, Waves,
  ChevronRight, ChevronLeft, Check, Users, Shield,
} from "lucide-react"
import { BRAND } from "../lib/brand"
import signupImg from "../assets/signup-img.jpg"
import { useLanguage } from "../lib/LanguageContext"
import LangToggle from "../components/LangToggle"

// Vietnam-Swim backend — owns auth, dashboards, and document storage (S3).
const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result.split(",")[1])
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

// ─── Static mock data (swap for API calls later) ────────────────────────────
const BLOOD_TYPES = ["A+", "A−", "B+", "B−", "AB+", "AB−", "O+", "O−"]



// Removed static mock data for CLUBS and COACHES_BY_CLUB

// Swimmer registration is a longer process (identity, password, DOB/blood
// type/documents, club pick, review), so it gets more, shorter phases than
// coach/club so nothing forces scrolling to reach the rest of a step.
const TOTAL_STEPS_BY_ROLE = { swimmer: 5, coach: 3, club: 2 }

// A swimmer's DOB anchors document verification, so it needs to be a real,
// plausible date, not just DD/MM/YYYY-shaped digits.
function isValidDob(value) {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value || "")
  if (!match) return false
  const [, dd, mm, yyyy] = match
  const day = Number(dd), month = Number(mm), year = Number(yyyy)
  const currentYear = new Date().getFullYear()
  if (year < currentYear - 100 || year > currentYear) return false
  const date = new Date(year, month - 1, day)
  // Date() rolls invalid days/months forward (e.g. 31/02 -> March 3rd) rather
  // than rejecting them, so round-trip the parts to catch that.
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StepBar({ step, total, t }) {
  return (
    <div className="mb-7">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-[.18em] text-mist-300">
        {t("signup_step_of").replace("{step}", step).replace("{total}", total)}
      </p>
      <div className="flex gap-1.5">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className="h-[3px] flex-1 transition-all duration-500"
            style={{
              background:
                i < step
                  ? "linear-gradient(90deg, #D9B36C, #E8CC8E)"
                  : "rgba(255,255,255,0.12)",
            }}
          />
        ))}
      </div>
    </div>
  )
}

function FieldLabel({ children }) {
  return (
    <span className="text-[10px] font-bold uppercase tracking-[.16em] text-fog-200">
      {children}
    </span>
  )
}

function FieldWrap({ label, children, half = false }) {
  return (
    <div className={`flex flex-col gap-1.5 ${half ? "flex-1 min-w-0" : ""}`}>
      <FieldLabel>{label}</FieldLabel>
      {children}
    </div>
  )
}

function TextInput({
  id, name, type = "text", placeholder, value, onChange,
  icon: Icon, autoComplete, required,
}) {
  const [showPwd, setShowPwd] = useState(false)
  const isPwd = type === "password"
  return (
    <div className="relative">
      {Icon && (
        <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-mist-400">
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
      )}
      <input
        id={id}
        name={name}
        type={isPwd ? (showPwd ? "text" : "password") : type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        required={required}
        className={`h-11 w-full border border-white/15 bg-ink-700/60 text-sm text-paper-0 placeholder:text-mist-400 transition-colors focus:border-gold-400/60 focus:outline-none focus:ring-1 focus:ring-gold-400/20 ${Icon ? "pl-[44px] pr-4" : "px-4"} ${isPwd ? "pr-11" : ""}`}
      />
      {isPwd && (
        <button
          type="button"
          onClick={() => setShowPwd((v) => !v)}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-mist-300 transition-colors hover:text-paper-0"
          aria-label={showPwd ? "Hide password" : "Show password"}
        >
          {showPwd
            ? <EyeOff className="h-4 w-4" aria-hidden="true" />
            : <Eye className="h-4 w-4" aria-hidden="true" />}
        </button>
      )}
    </div>
  )
}

function SelectInput({ id, name, value, onChange, options, placeholder, icon: Icon }) {
  return (
    <div className="relative">
      {Icon && (
        <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-mist-400">
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
      )}
      <select
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        className={`h-11 w-full appearance-none border border-white/15 bg-ink-700/60 text-sm text-paper-0 transition-colors focus:border-gold-400/60 focus:outline-none focus:ring-1 focus:ring-gold-400/20 ${Icon ? "pl-[44px] pr-[40px]" : "px-4 pr-[40px]"} ${!value ? "text-mist-400" : ""}`}
      >
        <option value="" disabled hidden>{placeholder}</option>
        {options.map((o) => (
          <option key={o.value ?? o} value={o.value ?? o} className="bg-ink-800 text-paper-0">
            {o.label ?? o}
          </option>
        ))}
      </select>
      {/* Chevron */}
      <span className="pointer-events-none absolute inset-y-0 right-3.5 flex items-center text-mist-400">
        <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M4.5 6L8 9.5 11.5 6" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" />
        </svg>
      </span>
    </div>
  )
}

function ErrorBox({ message }) {
  if (!message) return null
  return (
    <div role="alert" className="flex items-start gap-3 border border-red-500/30 bg-red-500/10 px-4 py-3">
      <svg className="mt-0.5 h-4 w-4 shrink-0 text-red-400" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 10.5a.875.875 0 1 1 0-1.75.875.875 0 0 1 0 1.75zM7.25 5.5a.75.75 0 0 1 1.5 0v3a.75.75 0 0 1-1.5 0v-3z" />
      </svg>
      <p className="text-sm font-medium text-red-300">{message}</p>
    </div>
  )
}

// ─── DOB picker (Auto-formatting text input) ─────────────────────────────────
function DOBInput({ value, onChange }) {
  const handleChange = (e) => {
    let raw = e.target.value.replace(/\D/g, "").slice(0, 8)
    let formatted = raw
    if (raw.length > 2 && raw.length <= 4) {
      formatted = raw.slice(0, 2) + "/" + raw.slice(2)
    } else if (raw.length > 4) {
      formatted = raw.slice(0, 2) + "/" + raw.slice(2, 4) + "/" + raw.slice(4)
    }
    onChange({ target: { name: "dob", value: formatted } })
  }

  return (
    <div className="relative">
      <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-mist-400">
        <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
      <input
        id="sw-dob"
        name="dob"
        type="text"
        placeholder="DD/MM/YYYY"
        value={value}
        onChange={handleChange}
        inputMode="numeric"
        className="h-11 w-full border border-white/15 bg-ink-700/60 pl-[44px] pr-4 text-sm text-paper-0 placeholder:text-mist-400 transition-colors focus:border-gold-400/60 focus:outline-none focus:ring-1 focus:ring-gold-400/20"
      />
    </div>
  )
}

// ─── Vietnamese phone input (+84 prefix) ─────────────────────────────────────
function VNPhoneInput({ id, name, value, onChange }) {
  // Strip the +84 prefix for display; store raw digits only
  const handleChange = (e) => {
    // Allow only digits, max 10
    let raw = e.target.value.replace(/\D/g, "").slice(0, 10)
    // Auto-format: 0XXX XXX XXXX
    let formatted = raw
    if (raw.length > 4 && raw.length <= 7) {
      formatted = raw.slice(0, 4) + " " + raw.slice(4)
    } else if (raw.length > 7) {
      formatted = raw.slice(0, 4) + " " + raw.slice(4, 7) + " " + raw.slice(7)
    }
    onChange({ target: { name, value: formatted } })
  }

  return (
    <div className="flex">
      {/* +84 prefix badge */}
      <span className="flex h-11 items-center gap-1.5 border border-r-0 border-white/15 bg-ink-700/40 px-3 text-sm font-semibold text-mist-200 shrink-0">
        <span className="text-base leading-none">🇻🇳</span>
        +84
      </span>
      <input
        id={id}
        name={name}
        type="tel"
        value={value}
        onChange={handleChange}
        placeholder="0912 345 678"
        autoComplete="tel"
        inputMode="numeric"
        className="h-11 min-w-0 flex-1 border border-white/15 bg-ink-700/60 pl-3 pr-4 text-sm text-paper-0 placeholder:text-mist-400 transition-colors focus:border-gold-400/60 focus:outline-none focus:ring-1 focus:ring-gold-400/20"
      />
    </div>
  )
}

// ─── Role tab strip ───────────────────────────────────────────────────────────

// ─── Step 1 forms per role ────────────────────────────────────────────────────

// The swimmer flow is long (identity + password + DOB/blood-type/documents),
// so it's split into three shorter phases instead of one scroll-heavy form.
function SwimmerBasicForm({ data, onChange }) {
  return (
    <div className="flex flex-col gap-4">
      <FieldWrap label="Full name">
        <TextInput id="sw-name" name="name" placeholder="Nguyen Van A" value={data.name} onChange={onChange} icon={User} autoComplete="name" />
      </FieldWrap>
      <FieldWrap label="Email">
        <TextInput id="sw-email" name="email" type="email" placeholder="you@club.org" value={data.email} onChange={onChange} icon={Mail} autoComplete="email" />
      </FieldWrap>
      <FieldWrap label="Phone number">
        <VNPhoneInput id="sw-phone" name="phone" value={data.phone} onChange={onChange} />
      </FieldWrap>
    </div>
  )
}

function SwimmerPasswordForm({ data, onChange }) {
  return (
    <div className="flex flex-col gap-4">
      <FieldWrap label="Password">
        <TextInput id="sw-password" name="password" type="password" placeholder="Min 6 characters" value={data.password} onChange={onChange} icon={Lock} autoComplete="new-password" />
      </FieldWrap>
      <FieldWrap label="Confirm password">
        <TextInput id="sw-confirm-password" name="confirmPassword" type="password" placeholder="Re-enter password" value={data.confirmPassword} onChange={onChange} icon={Lock} autoComplete="new-password" />
      </FieldWrap>
    </div>
  )
}

function SwimmerDetailsForm({ data, onChange, file, setFile, photo, setPhoto }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <FieldWrap label="Date of birth" half>
          <DOBInput value={data.dob} onChange={onChange} />
        </FieldWrap>
        <FieldWrap label="Blood type" half>
          <SelectInput id="sw-blood" name="blood" value={data.blood} onChange={onChange} placeholder="Select…" options={BLOOD_TYPES} icon={Droplets} />
        </FieldWrap>
      </div>
      <FieldWrap label="Birth Certificate / ID — PDF, PNG, or JPG (Required)">
        <input
          type="file"
          accept=".pdf,.png,.jpg,.jpeg"
          onChange={(e) => {
            const f = e.target.files[0]
            if (f && !["application/pdf", "image/png", "image/jpeg"].includes(f.type)) {
              alert("Please select a PDF, PNG, or JPG file.")
              e.target.value = ""
              setFile(null)
              return
            }
            if (f && f.size > 5 * 1024 * 1024) {
              alert("File must be less than 5MB.")
              e.target.value = ""
              setFile(null)
              return
            }
            setFile(f)
          }}
          className="w-full text-sm text-mist-300 file:mr-4 file:rounded-full file:border-0 file:bg-white/5 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-gold-400 hover:file:bg-white/10"
        />
      </FieldWrap>
      <FieldWrap label="Profile photo (Required)">
        <div className="flex items-center gap-4">
          {photo && (
            <img
              src={URL.createObjectURL(photo)}
              alt="Profile preview"
              className="h-14 w-14 rounded-full object-cover border border-white/15"
            />
          )}
          <input
            type="file"
            accept=".png,.jpg,.jpeg"
            onChange={(e) => {
              const f = e.target.files[0]
              if (f && f.size > 5 * 1024 * 1024) {
                alert("Photo must be less than 5MB.")
                e.target.value = ""
                setPhoto(null)
                return
              }
              setPhoto(f)
            }}
            className="w-full text-sm text-mist-300 file:mr-4 file:rounded-full file:border-0 file:bg-white/5 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-gold-400 hover:file:bg-white/10"
          />
        </div>
      </FieldWrap>
    </div>
  )
}

function CoachForm({ data, onChange }) {
  return (
    <div className="flex flex-col gap-4">
      <FieldWrap label="Full name">
        <TextInput id="co-name" name="name" placeholder="John Doe" value={data.name} onChange={onChange} icon={User} autoComplete="name" />
      </FieldWrap>
      <FieldWrap label="Email">
        <TextInput id="co-email" name="email" type="email" placeholder="you@club.org" value={data.email} onChange={onChange} icon={Mail} autoComplete="email" />
      </FieldWrap>
      <FieldWrap label="Phone number">
        <VNPhoneInput id="co-phone" name="phone" value={data.phone} onChange={onChange} />
      </FieldWrap>
      <FieldWrap label="Password">
        <TextInput id="co-password" name="password" type="password" placeholder="Min 6 characters" value={data.password} onChange={onChange} icon={Lock} autoComplete="new-password" />
      </FieldWrap>
      <FieldWrap label="Confirm password">
        <TextInput id="co-confirm-password" name="confirmPassword" type="password" placeholder="Re-enter password" value={data.confirmPassword} onChange={onChange} icon={Lock} autoComplete="new-password" />
      </FieldWrap>
    </div>
  )
}

function ClubForm({ data, onChange }) {
  return (
    <div className="flex flex-col gap-4">
      <FieldWrap label="Club name">
        <TextInput id="cl-clubname" name="clubName" placeholder="Shark Swim Club" value={data.clubName} onChange={onChange} icon={Building2} />
      </FieldWrap>
      <FieldWrap label="Location">
        <TextInput id="cl-location" name="location" placeholder="Hanoi, VN" value={data.location} onChange={onChange} icon={MapPin} />
      </FieldWrap>
      <FieldWrap label="Admin full name">
        <TextInput id="cl-adminname" name="adminName" placeholder="John Doe" value={data.adminName} onChange={onChange} icon={User} autoComplete="name" />
      </FieldWrap>
      <FieldWrap label="Admin email">
        <TextInput id="cl-adminemail" name="adminEmail" type="email" placeholder="admin@club.org" value={data.adminEmail} onChange={onChange} icon={Mail} autoComplete="email" />
      </FieldWrap>
      <FieldWrap label="Admin phone">
        <VNPhoneInput id="cl-adminphone" name="adminPhone" value={data.adminPhone} onChange={onChange} />
      </FieldWrap>
      <FieldWrap label="Admin password">
        <TextInput id="cl-adminpassword" name="adminPassword" type="password" placeholder="Min 6 characters" value={data.adminPassword} onChange={onChange} icon={Lock} autoComplete="new-password" />
      </FieldWrap>
      <FieldWrap label="Confirm admin password">
        <TextInput id="cl-confirm-adminpassword" name="confirmAdminPassword" type="password" placeholder="Re-enter password" value={data.confirmAdminPassword} onChange={onChange} icon={Lock} autoComplete="new-password" />
      </FieldWrap>
    </div>
  )
}

// ─── Step 2: Pick club + coach ────────────────────────────────────────────────

function PickClub({ role, clubId, setClubId, coachName, setCoachName, clubs }) {
  const coaches = clubs.find(c => c.id === clubId)?.coaches || []
  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm font-medium leading-relaxed text-mist-300">
        {role === "swimmer"
          ? "Choose the club you represent, then select your coach."
          : "Choose the club you'll be coaching at."}
      </p>

      {/* Club grid */}
      <div>
        <FieldLabel>Select your club</FieldLabel>
        <div className="mt-2 flex flex-col gap-2">
          {clubs.map((club) => (
            <button
              key={club.id}
              type="button"
              onClick={() => { setClubId(club.id); setCoachName("") }}
              className={`flex items-center gap-4 border px-4 py-3 text-left transition-colors ${clubId === club.id
                  ? "border-gold-500/60 bg-gold-500/10"
                  : "border-white/10 bg-ink-700/40 hover:border-white/25 hover:bg-ink-700/70"
                }`}
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center border text-[10px] transition-colors ${clubId === club.id
                    ? "border-gold-500 bg-gold-500 text-ink-900"
                    : "border-white/30 text-transparent"
                  }`}
              >
                <Check className="h-3 w-3" aria-hidden="true" />
              </span>
              <span className="flex-1">
                <span className="block text-sm font-semibold text-paper-0">{club.name}</span>
                <span className="block text-xs text-mist-300">{club.location}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Coach picker — swimmer only, shows after club selected */}
      {role === "swimmer" && clubId && (
        <div className="animate-[fadeUp_.25s_ease_both]">
          <FieldLabel>Select your coach</FieldLabel>
          <div className="mt-2 flex flex-col gap-2">
            {coaches.map((coach) => (
              <button
                key={coach}
                type="button"
                onClick={() => setCoachName(coach)}
                className={`flex items-center gap-4 border px-4 py-3 text-left transition-colors ${coachName === coach
                    ? "border-gold-500/60 bg-gold-500/10"
                    : "border-white/10 bg-ink-700/40 hover:border-white/25 hover:bg-ink-700/70"
                  }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center border text-[10px] transition-colors ${coachName === coach
                      ? "border-gold-500 bg-gold-500 text-ink-900"
                      : "border-white/30 text-transparent"
                    }`}
                >
                  <Check className="h-3 w-3" aria-hidden="true" />
                </span>
                <span className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-mist-400" aria-hidden="true" />
                  <span className="text-sm font-medium text-paper-0">{coach}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Step 3: Review ───────────────────────────────────────────────────────────

function ReviewRow({ label, value }) {
  if (!value) return null
  return (
    <div className="flex justify-between gap-4 border-b border-white/8 py-2.5 last:border-0">
      <span className="text-[11px] font-bold uppercase tracking-[.12em] text-mist-400">{label}</span>
      <span className="text-right text-sm font-medium text-paper-0">{value}</span>
    </div>
  )
}

function ReviewStep({ role, swimmerData, coachData, clubData, clubId, coachName, clubs }) {
  const club = clubs.find((c) => c.id === clubId)
  const isSwimmer = role === "swimmer"
  const isCoach = role === "coach"
  const isClub = role === "club"
  const d = isSwimmer ? swimmerData : isCoach ? coachData : clubData

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm font-medium text-mist-300">
        Please review your details before creating your account.
      </p>
      <div className="border border-white/10 bg-ink-700/30 px-4">
        <ReviewRow label="Role" value={isClub ? "Club" : role.charAt(0).toUpperCase() + role.slice(1)} />
        {isClub ? (
          <>
            <ReviewRow label="Club name" value={d.clubName} />
            <ReviewRow label="Location" value={d.location} />
            <ReviewRow label="Admin name" value={d.adminName} />
            <ReviewRow label="Admin email" value={d.adminEmail} />
            <ReviewRow label="Admin phone" value={d.adminPhone} />
          </>
        ) : (
          <>
            <ReviewRow label="Full name" value={d.name} />
            <ReviewRow label="Email" value={d.email} />
            <ReviewRow label="Phone" value={d.phone} />
            {isSwimmer && d.dob && <ReviewRow label="Date of birth" value={d.dob} />}
            {isSwimmer && d.blood && <ReviewRow label="Blood type" value={d.blood} />}
            {club && <ReviewRow label="Club" value={`${club.name} — ${club.location}`} />}
            {isSwimmer && coachName && <ReviewRow label="Coach" value={coachName} />}
          </>
        )}
      </div>
    </div>
  )
}

// ─── Main Signup page ─────────────────────────────────────────────────────────

const initialSwimmer = { name: "", email: "", phone: "", password: "", confirmPassword: "", dob: "", blood: "" }
const initialCoach = { name: "", email: "", phone: "", password: "", confirmPassword: "" }
const initialClub = { clubName: "", location: "", adminName: "", adminEmail: "", adminPhone: "", adminPassword: "", confirmAdminPassword: "" }

export default function Signup() {
  const [step, setStep] = useState(1)
  const [role, setRole] = useState("swimmer")
  const [swimmerData, setSwimmer] = useState(initialSwimmer)
  const [coachData, setCoach] = useState(initialCoach)
  const [clubData, setClub] = useState(initialClub)
  const [clubId, setClubId] = useState(null)
  const [coachName, setCoachName] = useState("")
  const [swimmerDoc, setSwimmerDoc] = useState(null)
  const [swimmerPhoto, setSwimmerPhoto] = useState(null)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [clubs, setClubs] = useState([])
  const { t } = useLanguage()

  useEffect(() => {
    fetch(`${API}/api/auth/clubs-coaches`)
      .then(r => r.json())
      .then(data => setClubs(data))
      .catch(e => console.error(e))
  }, [])

  // Warn before the browser back/close button silently discards a partially-filled wizard.
  useEffect(() => {
    if (step === 1 || done) return
    const handler = (e) => { e.preventDefault(); e.returnValue = "" }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [step, done])

  const isClub = role === "club"
  const isSwimmer = role === "swimmer"

  // When role changes, reset step to 1
  const handleRoleChange = (r) => {
    if (r !== role) { setRole(r); setStep(1); setError("") }
  }

  // Field change helpers
  const changeSwimmer = (e) => { setSwimmer((f) => ({ ...f, [e.target.name]: e.target.value })); setError("") }
  const changeCoach = (e) => { setCoach((f) => ({ ...f, [e.target.name]: e.target.value })); setError("") }
  const changeClub = (e) => { setClub((f) => ({ ...f, [e.target.name]: e.target.value })); setError("") }

  const totalSteps = TOTAL_STEPS_BY_ROLE[role]

  // Pick-club/coach step, shared by the swimmer and coach flows.
  const validatePickClub = () => {
    if (!clubId) return "Please select a club."
    if (isSwimmer && !coachName) return "Please select your coach."
    return ""
  }

  // Validate whichever phase is currently showing, per role.
  const validateCurrentStep = () => {
    if (isClub) {
      const { clubName, location, adminName, adminEmail, adminPhone, adminPassword, confirmAdminPassword } = clubData
      if (!clubName || !location || !adminName || !adminEmail || !adminPhone || !adminPassword)
        return "Please fill in all required fields."
      if (adminPassword.length < 6) return "Password must be at least 6 characters."
      if (adminPassword !== confirmAdminPassword) return "Passwords don't match."
      return ""
    }

    if (isSwimmer) {
      if (step === 1) {
        const { name, email, phone } = swimmerData
        if (!name || !email || !phone) return "Please fill in all required fields."
        return ""
      }
      if (step === 2) {
        const { password, confirmPassword } = swimmerData
        if (!password) return "Please fill in all required fields."
        if (password.length < 6) return "Password must be at least 6 characters."
        if (password !== confirmPassword) return "Passwords don't match."
        return ""
      }
      if (step === 3) {
        if (!isValidDob(swimmerData.dob)) return "Please enter a valid date of birth."
        if (!swimmerDoc) return "Birth Certificate or ID is required for swimmers."
        if (!swimmerPhoto) return "Profile photo is required for swimmers."
        return ""
      }
      if (step === 4) return validatePickClub()
      return ""
    }

    // coach
    if (step === 1) {
      const { name, email, phone, password, confirmPassword } = coachData
      if (!name || !email || !phone || !password) return "Please fill in all required fields."
      if (password.length < 6) return "Password must be at least 6 characters."
      if (password !== confirmPassword) return "Passwords don't match."
      return ""
    }
    if (step === 2) return validatePickClub()
    return ""
  }

  const handleNext = () => {
    const err = validateCurrentStep()
    if (err) { setError(err); return }
    setError("")
    setStep((s) => s + 1)
  }

  const handleBack = () => { setError(""); setStep((s) => s - 1) }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      // Build the registration payload based on role
      let payload = { role }

      if (isClub) {
        payload = {
          ...payload,
          name: clubData.adminName,
          email: clubData.adminEmail,
          phone: clubData.adminPhone,
          password: clubData.adminPassword,
          club_name: clubData.clubName,
          location: clubData.location,
        }
      } else if (role === "swimmer") {
        payload = {
          ...payload,
          name: swimmerData.name,
          email: swimmerData.email,
          phone: swimmerData.phone,
          password: swimmerData.password,
          dob: swimmerData.dob,
          blood: swimmerData.blood,
          club_id: clubId,
          coach_name: coachName,
        }
      } else {
        // coach
        payload = {
          ...payload,
          name: coachData.name,
          email: coachData.email,
          phone: coachData.phone,
          password: coachData.password,
          club_id: clubId,
        }
      }

      const res = await fetch(`${API}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })

      if (res.status === 409) {
        setError("An account with this email already exists.")
        return
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        // FastAPI 422s return `detail` as a list of {msg, loc, ...} objects, not a string.
        const detail = Array.isArray(data.detail)
          ? data.detail.map((d) => d.msg || JSON.stringify(d)).join(" ")
          : data.detail
        setError(detail || "Something went wrong. Please try again.")
        return
      }
      
      // Auto-login to bypass the success screen and go straight to pending
      try {
        const loginRes = await fetch(`${API}/api/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: payload.email, password: payload.password }),
        })

        if (loginRes.ok) {
          const loginData = await loginRes.json()
          localStorage.setItem("token", loginData.access_token)
          localStorage.setItem("role", loginData.role)
          localStorage.setItem("name", loginData.name)
          if (loginData.club_name) {
            localStorage.setItem("clubName", loginData.club_name)
          }

          if (role === "swimmer" && swimmerDoc) {
            try {
              const base64 = await fileToBase64(swimmerDoc)
              const certRes = await fetch(`${API}/api/me/birth-certificate`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${loginData.access_token}`,
                },
                body: JSON.stringify({ birth_certificate_base64: base64, content_type: swimmerDoc.type }),
              })
              if (!certRes.ok) throw new Error("Upload rejected")
            } catch (e) {
              console.error("Birth certificate upload failed:", e)
              sessionStorage.setItem("certUploadFailed", "1")
            }
          }

          if (role === "swimmer" && swimmerPhoto) {
            try {
              const base64 = await fileToBase64(swimmerPhoto)
              const photoRes = await fetch(`${API}/api/me/photo`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${loginData.access_token}`,
                },
                body: JSON.stringify({ photo_base64: base64 }),
              })
              if (!photoRes.ok) throw new Error("Upload rejected")
            } catch (e) {
              console.error("Profile photo upload failed:", e)
              sessionStorage.setItem("photoUploadFailed", "1")
            }
          }

          import("../lib/router").then(({ navigate }) => {
            navigate("/dashboard")
          })
          return
        }
      } catch (e) {
        console.error("Auto-login failed:", e)
      }

      setDone(true)
    } catch {
      setError("Could not connect to the server. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  // Step titles/subtitles per role, since swimmer/coach/club all have different phase counts.
  const STEP_TITLES = isClub
    ? { 1: t("signup_title_club_1"), 2: t("signup_title_club_2") }
    : isSwimmer
    ? {
        1: t("signup_title_swimmer_1"),
        2: t("signup_title_swimmer_2"),
        3: t("signup_title_swimmer_3"),
        4: t("signup_title_swimmer_4"),
        5: t("signup_title_swimmer_5"),
      }
    : { 1: t("signup_title_coach_1"), 2: t("signup_title_coach_2"), 3: t("signup_title_coach_3") }

  const STEP_SUBS = isClub
    ? { 1: t("signup_sub_club_1"), 2: t("signup_sub_club_2") }
    : isSwimmer
    ? {
        1: t("signup_sub_swimmer_1"),
        2: t("signup_sub_swimmer_2"),
        3: t("signup_sub_swimmer_3"),
        4: t("signup_sub_swimmer_4"),
        5: t("signup_sub_swimmer_5"),
      }
    : {
        1: t("signup_sub_coach_1"),
        2: t("signup_sub_coach_2"),
        3: t("signup_sub_coach_3"),
      }

  return (
    <div className="relative flex min-h-screen items-start justify-center overflow-hidden bg-ink-900 py-10">
      {/* Top right language toggle and signin */}
      <div className="absolute top-6 right-6 lg:right-10 lg:top-10 z-20 flex gap-4 items-center">
        <LangToggle />
        <Link
          to="/login"
          className="flex items-center justify-center border border-white/20 bg-ink-900/40 px-5 py-2 text-sm font-medium text-white transition-colors hover:border-white/40"
        >
          {t("signup_signin")}
        </Link>
      </div>

      {/* Back to Home */}
      <Link
        to="/"
        className="absolute left-5 top-6 z-50 flex items-center gap-2 border border-white/20 bg-ink-800/80 px-4 py-2 text-sm font-semibold text-paper-0 transition-colors hover:border-white/40 sm:left-8 sm:top-8"
      >
        <ChevronLeft className="h-4 w-4" />
        {t("signup_home")}
      </Link>

      {/* Background */}
      <div
        className="animate-hero-pan absolute inset-0 bg-cover"
        style={{
          backgroundImage: `url(${signupImg})`,
          backgroundPosition: "center 25%",
          opacity: 1,
          filter: "saturate(1) brightness(1)",
        }}
        aria-hidden="true"
      />
      {/* Gradient overlays to darken text area */}
      <div
        className="absolute inset-0 bg-ink-900/15"
        aria-hidden="true"
      />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent" aria-hidden="true" />

      <div className="relative z-10 w-full max-w-[480px] px-5">

        {/* ── Success screen ──────────────────────────────────────── */}
        {done ? (
          <div
            className="relative overflow-hidden border border-white/10 bg-ink-800/60 p-8 text-center shadow-2xl backdrop-blur-sm"
            style={{ boxShadow: "0 0 0 1px rgba(217,179,108,.06), 0 32px 64px -16px rgba(0,0,0,.6)" }}
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-[2px]" style={{ background: "linear-gradient(90deg, transparent, #D9B36C 50%, transparent)" }} aria-hidden="true" />
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center bg-gold-500/15 ring-1 ring-gold-500/40">
              <Check className="h-7 w-7 text-gold-400" />
            </div>
            <h2 className="mb-2 font-display text-2xl font-bold text-paper-0">You're all set!</h2>
            <p className="mb-6 text-sm font-medium text-mist-300">
              Your account request has been submitted. You'll receive a confirmation email shortly.
            </p>
            <Link
              to="/login"
              className="inline-block w-full bg-gold-500 py-3 text-center text-sm font-semibold tracking-[.04em] text-ink-900 transition-colors hover:bg-gold-400"
            >
              Go to sign in
            </Link>
          </div>
        ) : (
          /* ── Step card ─────────────────────────────────────────── */
          <div
            className="relative overflow-hidden border border-white/10 bg-ink-800/60 p-7 shadow-2xl backdrop-blur-sm"
            style={{ boxShadow: "0 0 0 1px rgba(217,179,108,.06), 0 32px 64px -16px rgba(0,0,0,.6)" }}
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-[2px]" style={{ background: "linear-gradient(90deg, transparent, #D9B36C 50%, transparent)" }} aria-hidden="true" />

            {/* Step bar */}
            <StepBar step={step} total={totalSteps} t={t} />

            {/* Title */}
            <div className="mb-6">
              <h1 className="font-display text-[1.6rem] font-bold leading-tight tracking-[-0.01em] text-paper-0">
                {STEP_TITLES[step]}
              </h1>
              <p className="mt-1 text-sm font-medium text-mist-300">{STEP_SUBS[step]}</p>
            </div>

            <form id="signup-form" onSubmit={step === totalSteps ? handleSubmit : (e) => { e.preventDefault(); handleNext() }} noValidate>
              {/* ── Step 1: identity (+ role picker) ──────────────────── */}
              {step === 1 && (
                <>
                  <div className="mb-6 flex border border-white/15" role="tablist" aria-label="Account type">
                    {[
                      { key: "swimmer", label: t("signup_role_swimmer"), Icon: User },
                      { key: "coach", label: t("signup_role_coach"), Icon: Shield },
                      { key: "club", label: t("signup_role_club"), Icon: Building2 },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        role="tab"
                        id={`tab-${key}`}
                        aria-selected={role === key}
                        onClick={() => handleRoleChange(key)}
                        className={`flex-1 py-2.5 text-xs font-semibold tracking-[.08em] uppercase transition-colors ${role === key
                            ? "bg-gold-500 text-ink-900"
                            : "text-mist-300 hover:bg-white/5 hover:text-paper-0"
                          }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {role === "swimmer" && <SwimmerBasicForm data={swimmerData} onChange={changeSwimmer} />}
                  {role === "coach" && <CoachForm data={coachData} onChange={changeCoach} />}
                  {role === "club" && <ClubForm data={clubData} onChange={changeClub} />}
                </>
              )}

              {/* ── Swimmer step 2: password ───────────────────────────── */}
              {isSwimmer && step === 2 && (
                <SwimmerPasswordForm data={swimmerData} onChange={changeSwimmer} />
              )}

              {/* ── Swimmer step 3: DOB/blood type/documents ───────────── */}
              {isSwimmer && step === 3 && (
                <SwimmerDetailsForm data={swimmerData} onChange={changeSwimmer} file={swimmerDoc} setFile={setSwimmerDoc} photo={swimmerPhoto} setPhoto={setSwimmerPhoto} />
              )}

              {/* ── Pick club (swimmer step 4 / coach step 2) ──────────── */}
              {((isSwimmer && step === 4) || (role === "coach" && step === 2)) && (
                <PickClub role={role} clubId={clubId} setClubId={setClubId} coachName={coachName} setCoachName={setCoachName} clubs={clubs} />
              )}

              {/* ── Review (final step for every role) ─────────────────── */}
              {step === totalSteps && (
                <ReviewStep
                  role={role}
                  swimmerData={swimmerData}
                  coachData={coachData}
                  clubData={clubData}
                  clubId={clubId}
                  coachName={coachName}
                  clubs={clubs}
                />
              )}

              {/* Error */}
              <div className="mt-4">
                <ErrorBox message={error} />
              </div>

              {/* Navigation buttons */}
              <div className={`mt-5 flex gap-3 ${step > 1 ? "justify-between" : "justify-end"}`}>
                {step > 1 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    id="signup-back"
                    className="flex h-11 items-center gap-2 border border-white/20 px-5 text-sm font-semibold text-paper-0 transition-colors hover:border-white/40 hover:bg-white/5"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                    {t("signup_back")}
                  </button>
                )}

                {/* Next OR Submit */}
                {step < totalSteps ? (
                  <button
                    type="submit"
                    id="signup-next"
                    className="flex h-11 flex-1 items-center justify-center gap-2 bg-gold-500 text-sm font-semibold tracking-[.04em] text-ink-900 transition-colors hover:bg-gold-400"
                  >
                    {t("signup_next")}
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    id="signup-submit"
                    disabled={loading}
                    className="flex h-11 flex-1 items-center justify-center gap-2 bg-gold-500 text-sm font-semibold tracking-[.04em] text-ink-900 transition-colors hover:bg-gold-400 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        {t("signup_creating")}
                      </>
                    ) : (
                      <>
                        {t("signup_submit")}
                        <Check className="h-4 w-4" aria-hidden="true" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {/* Sign in link */}
        <p className="mt-5 text-center text-sm font-medium text-gray-300">
          {t("signup_have_account")}{" "}
          <Link to="/login" id="go-to-login" className="font-semibold text-gold-400 transition-colors hover:text-gold-300">
            {t("signup_signin")}
          </Link>
        </p>

        {/* Footer */}
        <p className="mt-4 text-center text-[11px] font-medium text-gray-400">
          <Waves className="mb-0.5 mr-1 inline h-3 w-3" aria-hidden="true" />
          {BRAND.copyright}
        </p>
      </div>
    </div>
  )
}
